import _ from 'lodash';
import React, { useContext, useMemo, useRef, useState } from "react";
import { generateClient } from '@aws-amplify/api';
import { getAgentSettings as _getAgentSettings, getAIPlatforms as _getAIPlatforms } from '@/graphql/queries';
import { upsertAgentSetting as _upsertAgentSetting } from '@/graphql/mutations';
import HeaderWithTabs from '@/HeaderWithTabs'
import ErrorModal from '@/ErrorModal';
import Breadcrumb from '@/Breadcrumb';
import { checkboxField, selectField, textAreaField, textField } from '@/commonFields';
import useFormFields from '@/hooks/useFormFields';
import { agent_modalities, priorities } from './enums';
import RootContext from '@/RootContext';

const client = generateClient();

const getObject = ({ id }) => {
	return client.graphql({ query: _getAgentSettings, variables: { id } })
		.then(response => {
			const data = response.data.getAgentSettings.agent_settings?.[0];
			return data;
		});
};

const upsertObject = ({ input }) => {
	return client.graphql({ query: _upsertAgentSetting, variables: { input } })
		.then(response => {
			return response.data.upsertAgentSetting;
		});
};

const NULL_AI_PLATFORM = {
	id: null,
	name: 'None'
};

const getAIPlatforms = () => {
	return client.graphql({ query: _getAIPlatforms })
		.then(response => {
			return [
				NULL_AI_PLATFORM,
				...response.data.getAIPlatforms.ai_platforms
			];
		});
};

export default ({ createLabel = 'Create', links = [], onCancel, onSave, updateLabel = 'Save' }) => {
	const {
		origModel,
		model,
		setModel,
		changeModel,
		deleteModel,
		jsonModel,
		errors,
		setErrors,
		hasValue,
		getError,
		get_value,
		set_value,
		get_array,
		set_array,
		add_array,
		remove_array,
		id,
		isCreate,
		sanitizeObject
	} = useFormFields();

	const isConstructed = useRef(false);
	const rootContext = useContext(RootContext);
	const [errorMessage, setErrorMessage] = useState();
	const [isLoaded, setIsLoaded] = useState(isCreate);
	const [isSaving, setIsSaving] = useState(false);
	const isEqual = _.isEqualWith(sanitizeObject(model), origModel.current);
	const [file, setFile] = useState(null);

	const catchError = error => {
		if (error instanceof Error) {
			setErrorMessage(error.toString());
		} else {
			setErrorMessage((error.errors || []).map(e => e.message?.toString()));
		}
		setIsLoaded(true);
		setIsSaving(false);
	};

	if (!isConstructed.current) {
		isConstructed.current = true;
		if (!isLoaded) {
			getObject({ id })
				.then(data => {
					origModel.current = { agent_settings: data };
					setModel({ agent_settings: data });
					setIsLoaded(true);
				})
				.catch(catchError);
		} else {
			setModel({
				agent_settings: {
					ai_platform: null,
					priority: 0
				}
			});
		}
	}

	const details = useMemo(() => (
		<div className="row mt-5">
			<div className="col-8">
				<div className="border shadow p-3 bg-form">
					<div className="row">
						<div className="col">
							{textField({ autoFocus: isCreate, get: get_value, getError, set: set_value, key: 'agent_settings', label: 'Agent Name', name: 'agent_name' })}
						</div>
						<div className="col-3">
							{selectField({ get: get_value, getError, set: set_value, key: 'agent_settings', label: 'Priority', name: 'priority', options: priorities })}
						</div>
						<div className="col-3">
							{selectField({ defaultOptions: model.agent_settings ? [{ ai_platform: model.agent_settings.ai_platform, name: model.agent_settings.ai_platform || NULL_AI_PLATFORM.name }] : [], get: get_value, getData: getAIPlatforms, getError, set: set_value, key: 'agent_settings', label: 'AI Platform', name: 'ai_platform', label1Key: 'name', idKey: 'ai_platform', valueKey: 'ai_platform' })}
						</div>
					</div>
					<div className="row">
						<div className="col-3">
							{selectField({ get: get_value, getError, set: set_value, key: 'agent_settings', label: 'Agent Modality', name: 'agent_modality', options: agent_modalities })}
						</div>
						<div className="col mt-4">
							{checkboxField({ className: 'mb-1', get: get_value, getError, set: set_value, key: 'agent_settings', label: 'Supports Agent Prompt', name: 'supports_agent_prompt' })}
							{checkboxField({ className: 'mb-1', get: get_value, getError, set: set_value, key: 'agent_settings', label: 'Supports Text Intro', name: 'supports_text_intro' })}
						</div>
					</div>
					{textAreaField({ get: get_value, getError, set: set_value, key: 'agent_settings', label: 'Agent Executable', name: 'agent_executable', rows: 5 })}
				</div>
			</div>
		</div>
	), [JSON.stringify(errors), JSON.stringify(model)]);

	const tabs = [
		{ name: 'details', label: 'Details', content: details }
	];

	const handleSave = () => {
		// Extract input from model
		const input = {
			...changeModel.current,
			agent_settings: {
				...changeModel.current.agent_settings,
				...(!isCreate ? { id } : undefined)
			}
		};
		console.log('model', model);
		const newErrors = {
			agent_settings: {}
		};
		if (!hasValue({ key: 'agent_settings', name: 'agent_name' })) {
			newErrors.agent_settings.agent_name = 'Agent Name is required';
		}
		// Clean out empties
		Object.keys(newErrors).forEach(e => {
			if (
				(Array.isArray(newErrors[e]) && newErrors[e].length === 0) ||
				Object.keys(newErrors[e]).length === 0
			) {
				delete newErrors[e];
			}
		});
		console.log('newErrors', newErrors);
		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}
		console.log('input', input);
		console.log('deleteModel', deleteModel.current);
		setIsSaving(true);
		upsertObject({ input, ...(deleteModel.current.length > 0 ? { input_delete: deleteModel.current } : undefined) })
			.then(response => {
				console.log('response', response);
				setIsSaving(false);
				// Invalidate cache
				rootContext.set('agent_settings', null);
				onSave && onSave();
			})
			.catch(catchError);
	};

	const handleCancel = () => onCancel && onCancel();

	return (
		<>
			{errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage()} title="Error" />}
			<div className="container-fluid px-4 position-relative">
				<HeaderWithTabs
					isLoaded={isLoaded}
					path="/app/customers/"
					tabs={tabs}
				>
					<>
						{!isLoaded ? (
							<div className="text-center" style={{marginTop:'5rem'}}>
								<button className="btn btn-primary" type="button" disabled>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Loading...
								</button>
							</div>
						) : (
							<>
								{links.length > 0 && <Breadcrumb className="mb-3 mx-3" links={links} />}
								<div className="row align-items-center position-absolute py-1 me-5" style={{right:0}}>
									<button className="col-auto ml-auto my-0 btn btn-primary" disabled={!((isCreate || !isEqual) && !isSaving)} onClick={handleSave}>{isCreate ? createLabel : updateLabel }</button>
									<a className="col-auto" role="button" onClick={handleCancel}>Cancel</a>
								</div>
								{isSaving && (
									<div className="text-center position-absolute" style={{height:'100%',left:0,top:0, width:'100%',zIndex:1}}>
										<button className="btn btn-primary position-relative" style={{top:'40%'}} type="button" disabled>
											<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
											Saving...
										</button>
									</div>
								)}
							</>
						)}
					</>
				</HeaderWithTabs>
			</div>
		</>
	);
};
